import {
    DocumentInstanceDto,
    DocumentTemplateDto,
    DocumentTemplateListResponseDto,
    DocumentTemplateSearchDto,
    DynamicSectionDto,
    FormItemDto,
    FormItemKind,
    GenerateDocumentDto,
    OptionDto,
    PrefilledFormDto,
} from './document-generation.dto';
import { httpClient2 } from 'src/infrastructure/admi-client/http-client';

/** Recursively strips the internal `_id` field from form items before sending to the backend. */
function stripInternalIds(items: FormItemDto[]): FormItemDto[] {
    return items.map((item) => {
        const { _id: _, ...rest } = item;
        if (rest.kind === FormItemKind.SECTION) {
            const section = rest as Omit<DynamicSectionDto, '_id'>;
            return { ...section, items: stripInternalIds(section.items) } as FormItemDto;
        }
        return rest as FormItemDto;
    });
}

export const admiDocumentGenerationService = {
    getDocumentTemplates: async (search: DocumentTemplateSearchDto): Promise<DocumentTemplateListResponseDto> => {
        console.debug('search', search);

        const params: Record<string, string | number> = {
            page: search.page,
            size: search.size,
            sort: search.sort,
        };

        const response = await httpClient2.get<DocumentTemplateListResponseDto>('/v1/documents/templates', { params });

        return response.data;
    },

    createDocumentTemplate: async (template: DocumentTemplateDto): Promise<DocumentTemplateDto> => {
        const payload = { ...template, formSchema: stripInternalIds(template.formSchema) };
        const response = await httpClient2.post<DocumentTemplateDto>('/v1/documents/templates', payload);
        return response.data;
    },

    updateDocumentTemplate: async (template: DocumentTemplateDto): Promise<DocumentTemplateDto> => {
        const payload = { ...template, formSchema: stripInternalIds(template.formSchema) };
        const response = await httpClient2.put<DocumentTemplateDto>(`/v1/documents/templates/${template.id}`, payload);
        return response.data;
    },

    deleteDocumentTemplate: async (id: string): Promise<void> => {
        await httpClient2.delete(`/v1/documents/templates/${id}`);
    },

    getDocumentTemplateById: async (id: string): Promise<DocumentTemplateDto> => {
        const response = await httpClient2.get<DocumentTemplateDto>(`/v1/documents/templates/${id}`);
        return response.data;
    },

    generateDocument: async (generateDocument: GenerateDocumentDto) => {
        const response = await httpClient2.post<DocumentInstanceDto>('/v1/documents/generate', generateDocument);
        return response.data;
    },

    getPrefilledForm: async (templateId: string, context: Record<string, string>): Promise<PrefilledFormDto> => {
        const queryString = new URLSearchParams(context).toString();
        const url = `/v1/documents/prefill/${templateId}?${queryString}`;

        const response = await httpClient2.get<PrefilledFormDto>(url);
        return response.data;
    },

    getFieldOptions: async (
        optionsKey: string,
        query: string,
        dependencies: Record<string, any>,
    ): Promise<OptionDto[]> => {
        const params: Record<string, string> = {
            key: optionsKey,
            query: query,
            ...Object.entries(dependencies || {}).reduce(
                (acc, [k, v]) => {
                    if (v !== undefined && v !== null) {
                        acc[k] = String(v);
                    }
                    return acc;
                },
                {} as Record<string, string>,
            ),
        };

        const response = await httpClient2.get<OptionDto[]>('/v1/documents/options', { params });
        return response.data;
    },
};
